import React, { useEffect, useState } from 'react';
import { Placement } from '@popperjs/core';

import { FieldSize } from 'ds/types';
import { DateTimeFormat, yyyyMmDdToDate } from 'shared';

import DatePickerCalendar from './DatePickerCalendar';
import DatePickerContainer from './DatePickerContainer';
import TimeRangeInputs, { Props as TimeRangeInputsProps } from './TimeRangeInputs';
import { CalendarDisplayState, DatePickerProps } from './types';

export interface Props extends DatePickerProps {
  size?: FieldSize;
  label?: string;
  placeholder?: string;
  dateTimeFormat?: DateTimeFormat;
  disabled?: boolean;
  isBottomSheet?: boolean;
  type?: 'marketing';
  required?: boolean;
  placement?: Placement;
  setCalendarElement?: (calendar: HTMLDivElement | null) => void;
  bottomSheetZIndexPosition?: number;
  isReadOnly?: boolean;
  excludeDates?: Date[];
  timeRangeProps?: TimeRangeInputsProps;
}

const DatePicker: React.FC<Props> = ({
  label,
  onChange,
  size,
  placeholder,
  allowPastDates,
  dateTimeFormat = 'MED_WITH_SHORT_WEEKDAY',
  disabled,
  isBottomSheet,
  onlyWeekdays,
  required,
  type,
  placement,
  setCalendarElement,
  bottomSheetZIndexPosition,
  isReadOnly,
  excludeDates,
  timeRangeProps,
  autoFocus,
  ...props
}) => {
  const [calendarState, setCalendarState] = useState<CalendarDisplayState>('hidden');
  const onClear = () => {
    onChange(null);
  };

  useEffect(() => {
    if (autoFocus) {
      if (isBottomSheet) {
        setCalendarState('visible');
      } else {
        setCalendarState('opening');

        setTimeout(() => {
          setCalendarState('visible');
        }, 300);
      }
    }
  }, [autoFocus, isBottomSheet]);

  const selected = typeof props.selected === 'string' ? yyyyMmDdToDate(props.selected) : props.selected;
  const minDate = typeof props.minDate === 'string' ? yyyyMmDdToDate(props.minDate) : props.minDate;
  const maxDate = typeof props.maxDate === 'string' ? yyyyMmDdToDate(props.maxDate) : props.maxDate;

  return (
    <DatePickerContainer
      selected={selected}
      dateTimeFormat={dateTimeFormat}
      size={size}
      calendarState={calendarState}
      setCalendarState={setCalendarState}
      placeholder={placeholder}
      onClear={onClear}
      calendarClassName="DatePickerCalendar"
      label={label}
      isBottomSheet={isBottomSheet}
      type={type}
      required={required}
      placement={placement}
      setCalendarElement={setCalendarElement}
      bottomSheetZIndexPosition={bottomSheetZIndexPosition}
      disabled={disabled && !isReadOnly}
      isReadOnly={isReadOnly}
    >
      {timeRangeProps && <TimeRangeInputs {...timeRangeProps} />}
      <DatePickerCalendar
        selected={selected}
        onChange={date => {
          onChange(date);
          setCalendarState('hidden');
        }}
        autoFocus={autoFocus}
        minDate={allowPastDates && !props.minDate ? undefined : minDate || new Date()}
        maxDate={maxDate}
        onlyWeekdays={onlyWeekdays}
        excludeDates={excludeDates}
        openToDate={props.openToDate}
      />
    </DatePickerContainer>
  );
};

export default DatePicker;
